<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  invoice: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['verify', 'void', 'newLink', 'refund']);

const STATUS_STYLES = {
  open: 'bg-n-amber-3 text-n-amber-11',
  paid: 'bg-n-teal-3 text-n-teal-11',
  void: 'bg-n-alpha-2 text-n-slate-11',
  refunded: 'bg-n-alpha-2 text-n-slate-11',
};

const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');

const statusClass = computed(
  () => STATUS_STYLES[props.invoice.status] || STATUS_STYLES.void
);

const isOpen = computed(() => props.invoice.status === 'open');

const hasPendingPayment = computed(() =>
  (props.invoice.payments || []).some(payment => payment.status === 'pending')
);

const formattedAmount = computed(
  () =>
    `${props.invoice.currency} ${(props.invoice.total_cents / 100).toFixed(2)}`
);

const itemSummary = computed(() => {
  const names = (props.invoice.items || []).map(item => item.name);
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
});

const createdDate = computed(() =>
  new Date(props.invoice.created_at).toLocaleDateString()
);
</script>

<template>
  <div
    class="flex flex-col gap-1.5 p-3 rounded-xl border border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-medium text-n-slate-12 tabular-nums">
        {{ invoice.number }}
      </span>
      <span
        class="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full"
        :class="statusClass"
      >
        {{ $t(`INVOICES.STATUS.${invoice.status.toUpperCase()}`) }}
      </span>
    </div>
    <div class="flex items-center justify-between gap-2">
      <span class="text-base font-semibold text-n-slate-12 tabular-nums">
        {{ formattedAmount }}
      </span>
      <span class="text-xs text-n-slate-10">{{ createdDate }}</span>
    </div>
    <span v-if="itemSummary" class="text-xs text-n-slate-11 truncate">
      {{ itemSummary }}
    </span>
    <div v-if="isOpen" class="flex items-center gap-1.5 mt-1">
      <NextButton
        v-if="hasPendingPayment"
        :label="$t('INVOICES.ACTIONS.VERIFY')"
        slate
        faded
        xs
        @click="emit('verify')"
      />
      <NextButton
        v-else
        :label="$t('INVOICES.ACTIONS.NEW_LINK')"
        slate
        faded
        xs
        @click="emit('newLink')"
      />
      <NextButton
        :label="$t('INVOICES.ACTIONS.VOID')"
        ruby
        ghost
        xs
        @click="emit('void')"
      />
    </div>
    <div
      v-else-if="invoice.status === 'paid' && isAdmin"
      class="flex items-center gap-1.5 mt-1"
    >
      <NextButton
        :label="$t('INVOICES.ACTIONS.REFUND')"
        ruby
        ghost
        xs
        @click="emit('refund')"
      />
    </div>
  </div>
</template>
